import { Component, Element, h, Host, Listen, Prop } from '@stencil/core';
import {
	ContentTypes,
	ImageOrientationType,
} from '../../../../_storybook-helpers/image-with-cta';
import { Breakpoint, getCurrentBreakpoint } from '../_helpers/breakpoints';
import {
	moveInlineStylesToHead,
	removeInlineStylesFromHead,
} from '../_helpers/content-security-policy';

@Component({
	tag: 'ds-mid-page-banner',
})
export class MidPageBanner {
	@Prop() appearanceType: string;
	@Prop() imagePosition: ImageOrientationType;
	@Prop() isImageHiddenOnMobile: boolean;
	@Prop() appearanceSize: 'small' | 'standard';
	@Prop() contentType: ContentTypes;

	private readonly windowResizeDebounceDelay = 50;
	private resizeTimeout = 0;
	@Element() el: HTMLDsMidPageBannerElement;
	imgColumn: HTMLElement | undefined;
	imgWithAccent: HTMLElement | undefined;
	img: HTMLElement | undefined;
	contentContainer: HTMLElement | undefined;
	pullQuoteText: HTMLElement | undefined;

	@Listen('resize', { target: 'window', passive: true })
	onWindowResize(): void {
		window.clearTimeout(this.resizeTimeout);
		this.resizeTimeout = window.setTimeout(
			() => [
				this.hideContainer(),
				!this.appearanceType.includes('illustration') &&
					this.setVerticalAlignment(),
				this.setClassForPq(),
				this.appearanceType.includes('illustration') &&
					this.setIllustrationVerticalAlignment(),
			],
			this.windowResizeDebounceDelay
		);
	}

	componentDidLoad() {
		this.imgColumn = this.el.querySelector('div.ds-img-column');
		this.imgWithAccent = this.el.querySelector('div.ds-img-with-accent');
		this.img = this.el.querySelector('ds-image-with-alternative');
		this.contentContainer = this.el.querySelector('div.ds-content-column');
		this.pullQuoteText = this.el.querySelector('div.ds-pull-quote-text');
		!this.appearanceType.includes('illustration') &&
			this.setVerticalAlignment();
		this.hideContainer();
		this.setClassForPq();
		this.appearanceType.includes('illustration') &&
			this.setIllustrationVerticalAlignment();
		moveInlineStylesToHead(this.el.id);
	}

	componentDidUpdate() {
		moveInlineStylesToHead(this.el.id);
	}

	disconnectedCallback() {
		removeInlineStylesFromHead(this.el.id);
	}

	// method for setting vertical alignment if image is larger than content
	setVerticalAlignment(): void {
		const children: HTMLCollection = this.contentContainer.children;
		const bottomPadding = window
			.getComputedStyle(this.imgWithAccent)
			.getPropertyValue('padding-bottom');
		const imgHeight =
			this.img.getBoundingClientRect().height + parseInt(bottomPadding, 10);

		let contentItemsHeight = 0;
		for (let i = 0; i < children.length; i++) {
			contentItemsHeight += children[i].getBoundingClientRect().height;
		}

		if (imgHeight > contentItemsHeight) {
			this.contentContainer.style.justifyContent = 'center';
			this.contentContainer.style.paddingBottom = bottomPadding;
		} else {
			this.contentContainer.style.paddingBottom = '0';
		}

		moveInlineStylesToHead(this.el.id);
	}

	setIllustrationVerticalAlignment = () => {
		const imgColumnHeight = this.imgColumn.offsetHeight;
		const contentColumnHeight = this.contentContainer.offsetHeight;

		if (imgColumnHeight > contentColumnHeight) {
			this.contentContainer.classList.add('ds-center-vertically');
		} else {
			this.contentContainer.classList.remove('ds-center-vertically');
		}
		moveInlineStylesToHead(this.el.id);
	};

	// method for hiding image container on XS breakpoint
	hideContainer = (): void => {
		switch (getCurrentBreakpoint()) {
			case Breakpoint.XS:
			case Breakpoint.SM:
				if (this.isImageHiddenOnMobile) {
					this.imgColumn.style.display = 'none';
					this.contentContainer.classList.remove('col-sm-7');
					this.contentContainer.classList.add('col-sm-12');
				}
				break;
			default:
				this.imgColumn.style.display = 'unset';
				this.contentContainer.classList.remove('col-sm-12');
				this.contentContainer.classList.add('col-sm-7');
		}
	};

	// method for setting PQ class based on appearance size
	setClassForPq(): void {
		if (this.pullQuoteText) {
			switch (this.appearanceSize) {
				case 'small':
					this.pullQuoteText.classList.remove(
						'ds-heading-m-lc',
						'ds-body-l-bold'
					);
					this.pullQuoteText.classList.add('ds-heading-s-lc');
					break;
				case 'standard':
					this.pullQuoteText.classList.remove(
						'ds-heading-s-lc',
						'ds-body-l-bold'
					);
					this.pullQuoteText.classList.add('ds-heading-m-lc');
					break;
			}
		}
	}

	render() {
		return (
			<Host>
				<slot></slot>
			</Host>
		);
	}
}
